import React, { useMemo } from 'react';
import './JobTable.less';
import { JobType } from '../../../redux/jobs/jobs.types';
import Table from '../../../components/envago/Table/Table';
import { resolveMeterType } from '../../../helper/MeterHelper';
import EnergyIcon from '../../../components/envago/EnergyIcon/EnergyIcon';
import Icon from '../../../components/envago/Icon/Icon';
import { splitJoin } from '../../../helper/TextHelper';
import OptionalRow from '../../../components/envago/OptionalRow/OptionalRow';
import { useTranslation } from 'react-i18next';
import { hasMultipleValues } from '../../../helper/GroupHelper';
import { ColumnDef } from '@tanstack/react-table';
import { useSelector } from 'react-redux';
import { applicationSelectors } from '../../../redux/application';
import { applyColumnConfig, hasDataColumn } from './applyColumnConfig';
import { hasVisibleField } from '../../../fieldVisibility/fieldVisibility';
import { useFieldVisibility } from '../../../fieldVisibility/useFieldVisibility';


const JobTable = ({ jobs, rowActions }: { jobs: JobType[], rowActions?: (job: JobType) => (void) }) => {

    const { t } = useTranslation();

    // Diese Tabelle ist immer die Tabelle des Zugangs-Details, deshalb kein Prop.
    // Die Config bleibt für die Reihenfolge nötig, die Sichtbarkeit kommt aus
    // der Vereinigung mit der portalweiten Liste.
    const jobTableConfig = useSelector(applicationSelectors.getJobTableConfig('sharedJobs'));
    const visibility = useFieldVisibility('sharedJobs');

    const showColumns = useMemo(() => {
        return {
            'meter.meterAddress.objectKey': hasMultipleValues(jobs, 'meter.meterAddress.objectKey'),
        }
    }, [jobs]);

    const columns = useMemo(
        () => {
            const field = visibility.pick;

            // „Zu sehen" heißt: die Angabe ist nicht ausgeblendet UND von ihren
            // Bestandteilen ist mindestens einer übrig. Genau diese Zeilen
            // entscheiden auch, ob die zusammengefasste Spalte überhaupt
            // entsteht.
            const showCompany = !visibility.isHidden('customer.company');
            const showName =
                !visibility.isHidden('customer.name') &&
                hasVisibleField(visibility, ['customer.firstname', 'customer.lastname']);

            // 'meter.meterAddress.street' ist die Straßenangabe samt Hausnummer;
            // einen Key für „nur den Straßennamen" gibt es bewusst nicht.
            const showStreet = !visibility.isHidden('meter.meterAddress.street');
            // Die Hausnummer hält die Zelle NICHT am Leben: ohne Straße hätte
            // sie keinen Bezug.
            const showAddress = hasVisibleField(visibility, [
                'meter.meterAddress.street',
                'meter.meterAddress.zip',
                'meter.meterAddress.city',
            ]);

            return applyColumnConfig(
                [
                    {
                        header: t('meterReadingJob.type'),
                        id: 'meter.meterType',
                        cell: (props: any) => {
                            const meterType = resolveMeterType(props.row.original.meter);
                            return <EnergyIcon iconClassName={'h-5 w-5'} meterType={meterType} />;
                        },
                        enableSorting: false,
                        enableGrouping: false,
                        size: 40,
                    },
                    {
                        id: 'job.status',
                        header: t('meterReadingJob.status'),
                        size: 50,
                        enableSorting: false,
                        enableGrouping: false,
                        cell: (props: any) => {
                            return <div>{props.row.original.done ?
                                <Icon path={Icon.Path.mdiCheckCircleOutline} className={'h-5 text-green-500'} /> :
                                <Icon path={Icon.Path.mdiCheckboxBlankCircleOutline} className={'h-5 text-gray-300'} />
                            }</div>;
                        },
                    },
                    {
                        id: 'meter.meterNumber',
                        header: t('meterReadingJob.meterNumber'),
                        accessorFn: (row: JobType) => row.meter.meterNumber,
                        enableSorting: false,
                        enableGrouping: false,
                    },
                    showColumns['meter.meterAddress.objectKey'] && {
                        id: 'meter.meterAddress.objectKey',
                        header: t('meterReadingJob.objectKey'),
                        accessor: 'meter.meterAddress.objectKey',
                        enableSorting: false,
                        enableGrouping: false,
                    },
                    (showCompany || showName) && {
                        id: 'customer.summary',
                        header: t('meterReadingJob.customer.title'),
                        canSort: false,
                        enableGrouping: false,
                        cell: (props: any) => {
                            const customer = props.row.original.customer;

                            return <div className={'leading-none my-2'}>
                                <OptionalRow className={''}>{showCompany ? customer?.company : undefined}</OptionalRow>
                                <OptionalRow>{showName ? splitJoin(' ', field('customer.firstname', customer?.firstname), field('customer.lastname', customer?.lastname)) : undefined}</OptionalRow>
                            </div>;
                        },
                    },
                    showAddress && {
                        id: 'meter.meterAddress.full',
                        header: t('meterReadingJob.address.title'),
                        canSort: false,
                        enableGrouping: false,
                        cell: (props: any) => {
                            const address = props.row.original.meter.meterAddress;

                            return <div className={'leading-none'}>
                                <OptionalRow>
                                    {showStreet ? splitJoin(' ', address?.street, field('meter.meterAddress.houseNumber', address?.houseNumber)) : undefined}
                                </OptionalRow>
                                <OptionalRow>
                                    {splitJoin(' ', field('meter.meterAddress.zip', address?.zip), field('meter.meterAddress.city', address?.city))}
                                </OptionalRow>
                            </div>;
                        },
                    },
                    // Bedienelement ohne Registry-Key: von der Config unberührt.
                    rowActions && {
                        id: 'actions',
                        size: 40,
                        canSort: false,
                        enableGrouping: false,
                        cell: (props: any) => rowActions(props.row.original),
                    }
                ].filter(a => !!a).map(a => a as ColumnDef<any>),
                visibility,
                jobTableConfig,
                'sharedJobs',
            );
        },
        [t, showColumns, rowActions, visibility, jobTableConfig],
    );

    return (
        <div className='job-table w-full flex-1 flex' style={{minHeight: 200}}>
            {hasDataColumn(columns) ? (
                <Table
                    className={'text-sm flex-1'}
                    selectable={false}
                    groupable={false}
                    columns={columns}
                    data={jobs}
                />
            ) : (
                // Eine Tabelle mit Kopf und ohne Datenspalten sieht wie ein
                // Ladefehler aus. Der Satz sagt, dass es an der Config liegt.
                <div className={'text-sm text-gray-500'}>
                    {t('management.accounts.share.tableColumns.allHidden')}
                </div>
            )}
        </div>
    );
};

export default JobTable;
